import { z } from 'zod';
import { audienceSchema, activityStatuses } from './program.js';

/**
 * Publiczne API agendy `/api/public/v1` dla koderek.edu.pl (PRD 18 US-901, ADR 05 §2.3). Bez danych uczestników i e‑maili
 * (BR-44, BR-80). Zmiana niezgodna wstecz = nowa wersja ścieżki (AC-7). Czasy w ISO 8601 z przesunięciem strefy (AC-2).
 */
export const publicEditionSchema = z.object({
  slug: z.string(),
  name: z.string(),
  city: z.string(),
  address: z.string(),
  /** Krótka nazwa miejsca, np. „SP Kopernika” (opcjonalna) – odróżnia edycje w tym samym mieście tego samego dnia. */
  placeName: z.string().nullable(),
  /** Dzień wydarzenia `YYYY-MM-DD` (Europe/Warsaw). */
  date: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  registrationOpensAt: z.string(),
  status: z.enum(['published', 'finished']),
  /** Agenda w aplikacji – link do umieszczenia na stronie (US-902 AC-1). */
  agendaUrl: z.string(),
  /** Agenda edycji w tym API. */
  apiUrl: z.string(),
});
export type PublicEdition = z.infer<typeof publicEditionSchema>;

export const publicEditionListSchema = z.object({ editions: z.array(publicEditionSchema) });
export type PublicEditionList = z.infer<typeof publicEditionListSchema>;

export const publicActivitySchema = z.object({
  symbol: z.string(),
  title: z.string(),
  /** HTML oczyszczony przy zapisie (tylko podstawowe formatowanie). */
  description: z.string().nullable(),
  // Bez `requirements` – to wymagania prowadzącego dla organizatorów, nie informacja publiczna.
  type: z.string(),
  room: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  audience: audienceSchema,
  ageMin: z.number().int().nullable(),
  ageMax: z.number().int().nullable(),
  status: z.enum(activityStatuses),
  cancelReason: z.string().nullable(),
  /** Czy na zajęcia są zapisy; bez zapisów – wstęp wolny. */
  enrollment: z.boolean(),
  capacity: z.number().int(),
  reserve: z.number().int(),
  enrolled: z.number().int(),
  /** Wolne miejsca łącznie z rezerwą; null – wstęp wolny. */
  freeSeats: z.number().int().nullable(),
  speakers: z.string().nullable(),
  speakerBios: z.array(z.object({ name: z.string(), bio: z.string() })),
  /** Szczegóły zajęć w aplikacji (US-902 AC-1). */
  url: z.string(),
});
export type PublicActivity = z.infer<typeof publicActivitySchema>;

export const publicAgendaSchema = z.object({
  edition: publicEditionSchema,
  rooms: z.array(z.object({ name: z.string(), order: z.number().int() })),
  types: z.array(z.object({ name: z.string(), color: z.string(), enrollment: z.boolean() })),
  activities: z.array(publicActivitySchema),
});
export type PublicAgenda = z.infer<typeof publicAgendaSchema>;
